import styled from '@emotion/styled';
import dayjs from 'lib/dayjs';
import { HTMLAttributes } from 'react';
import { DepartmentsSelect } from 'views/Dashboard/ImplementData/components/Filter/DepartmentSelect';
import {
  usePlatformSelect,
  useStoreInfoSelect,
  useStoreTypeSelect,
} from 'views/Dashboard/ImplementData/components/Filter/Provider';
import { DashboardFilterType } from 'views/Dashboard/ImplementData/components/Filter/useFilter';
import { useCustomSelect } from '../components/AssistantPerf/Content';
import { DatePicker } from 'components/DatePicker';

export interface FilterValue {
  receptionType: Dashboard.ReceptionScore.ReceptionType;
  departments?: Dashboard.Department[];
  platform?: General.PlatformUpperCase;
  storeType?: Dashboard.ReceptionScore.StoreType;
  storeInfo: DashboardFilterType['storeInfo'];
  assistantIds: string[];
  assistantId?: string;
  startTime: number;
  endTime: number;
}

export interface FilterProps extends HTMLAttributes<HTMLDivElement> {
  singleAssistant?: boolean;
  filterValue: FilterValue;
  onFilterValueChange: (value: FilterValue) => void;
}

function Filter(props: FilterProps) {
  const { singleAssistant = false, filterValue, onFilterValueChange, ...otherProps } = props;

  const {
    departments,
    platform,
    storeType,
    storeInfo,
    assistantIds,
    assistantId,
    startTime,
    endTime,
  } = filterValue;

  const departmentSelect = (
    <DepartmentsSelect
      dashboardType="performance"
      value={departments}
      onChange={(departments) => {
        onFilterValueChange({
          ...filterValue,
          departments,
          platform: undefined,
          storeType: undefined,
          storeInfo: {},
          assistantIds: [],
          assistantId: undefined,
        });
      }}
    />
  );

  const { regionInfo, platformOptions, platformSelect } = usePlatformSelect({
    departments,
    scene: 'ASSISTANT_PERFORMANCE',
    showPlatformAll: true,
    value: platform || 'ALL',
    onChange: (platform) => {
      onFilterValueChange({
        ...filterValue,
        platform: platform === 'ALL' ? undefined : platform,
        storeInfo: {},
      });
    },
  });

  const { storeTypeSelect } = useStoreTypeSelect({
    value: storeType,
    onChange: (storeType) => {
      onFilterValueChange({
        ...filterValue,
        storeType,
        storeInfo: {},
      });
    },
  });

  const { storeInfoSelect } = useStoreInfoSelect({
    regionInfo,
    platform: platform || 'ALL',
    departments,
    platformOptions,
    storeType,
    storeInfo,
    onChange: (storeInfo) => {
      onFilterValueChange({
        ...filterValue,
        storeInfo,
      });
    },
    scene: 'ASSISTANT_PERFORMANCE',
  });

  const { customSelect } = useCustomSelect({
    departments,
    ...(singleAssistant
      ? {
          mode: undefined,
          value: assistantId,
          onChange: (assistantId) => {
            onFilterValueChange({
              ...filterValue,
              assistantId,
            });
          },
        }
      : {
          mode: 'multiple',
          value: assistantIds,
          onChange: (assistantIds) => {
            onFilterValueChange({
              ...filterValue,
              assistantIds,
            });
          },
        }),
  });

  const rangeSelect = (
    <DatePicker.RangePicker
      allowClear={false}
      value={[dayjs(startTime), dayjs(endTime)]}
      onChange={(value) => {
        const [start, end] = value || [];

        if (!start || !end) {
          return;
        }

        onFilterValueChange({
          ...filterValue,
          startTime: start.startOf('days').valueOf(),
          endTime: end.endOf('days').valueOf(),
        });
      }}
      disabledDate={(date) => {
        return (
          date.startOf('days').isAfter(dayjs()) ||
          date.startOf('days').isBefore(dayjs().subtract(90, 'days'))
        );
      }}
    />
  );

  return (
    <Container {...otherProps}>
      <section>
        {departmentSelect}
        {platformSelect}
        {storeTypeSelect}
        {storeInfoSelect}
        {customSelect}
      </section>
      {rangeSelect}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 16px 12px;
  background: #fff;
  > section {
    flex: 1 0 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    > div {
      min-width: 142px;
    }
    > * {
      margin: 12px 16px 0 0;
    }
  }
  > div {
    flex: 0 0 auto;
    margin-top: 12px;
  }
`;

export default Filter;
